import React, { useState, useMemo } from 'react';
import MainLayout from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Plus, FolderKanban } from 'lucide-react';
import { useSupabaseStore } from '@/store/SupabaseStore';
import CreateProjectDialog from '@/components/projects/CreateProjectDialog';
import ProjectCard from '@/components/projects/ProjectCard';
import LoadingScreen from '@/components/ui/loading-screen';
import { Project } from '@/types';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

const Projects = () => {
  const { projects, loadingProjects, chats } = useSupabaseStore();
  const [isCreateDialogOpen, setCreateDialogOpen] = useState(false);
  const { user } = useAuth();
  const [docCounts, setDocCounts] = useState<Record<string, number>>({});

  React.useEffect(() => {
    const fetchDocCounts = async () => {
      if (!user?.id || !Array.isArray(projects) || projects.length === 0) {
        setDocCounts({});
        return;
      }
      const projectIds = projects.map(p => p.id);
      const { data, error } = await supabase
        .from('mapleword_project_documents')
        .select('project_id')
        .eq('user_id', user.id)
        .in('project_id', projectIds);
      if (error) {
        setDocCounts({});
        return;
      }
      const counts: Record<string, number> = {};
      (data || []).forEach((row: any) => {
        const pid = row.project_id;
        counts[pid] = (counts[pid] || 0) + 1;
      });
      setDocCounts(counts);
    };
    fetchDocCounts();
  }, [projects, user?.id]);

  const projectsWithChatCount = useMemo(() => {
    return projects.map((project: Project) => ({
      ...project,
      chatCount: chats.filter(chat => chat.project_id === project.id).length,
      docCount: docCounts[project.id] || 0,
    }));
  }, [projects, chats, docCounts]);

  return (
    <MainLayout>
      <div className="container px-4 py-6 max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold">Projects</h1>
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" /> New Project
          </Button>
        </div>

        {loadingProjects ? (
          <div className="flex justify-center items-center h-64">
            <LoadingScreen />
          </div>
        ) : projectsWithChatCount.length > 0 ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {projectsWithChatCount.map(project => (
              <ProjectCard key={project.id} project={project} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12 rounded-lg ring-1 ring-muted-foreground/20 bg-muted/10">
            <FolderKanban className="mx-auto h-12 w-12 text-muted-foreground" />
            <h2 className="mt-4 text-xl font-semibold">No projects yet</h2>
            <p className="text-muted-foreground mt-2">Create your first project to organize your chats.</p>
            <Button onClick={() => setCreateDialogOpen(true)} className="mt-6">
              <Plus className="mr-2 h-4 w-4" /> Create Project
            </Button>
          </div>
        )}
      </div>
      <CreateProjectDialog open={isCreateDialogOpen} onOpenChange={setCreateDialogOpen} />
    </MainLayout>
  );
};

export default Projects;